import { useState, useCallback } from 'react';
import {
  Popover,
  Box,
  Typography,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useShallow } from 'zustand/react/shallow';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import {
  useDeductionsUIStore,
  type DeductionGroupingMode,
} from '../stores/deductionsUIStore';

/**
 * DeductionGroupingMenu — Linear-style "Display" popover that lets users
 * switch the deduction list grouping between "Worker / Gang" and "Site".
 * Reads and writes `groupingMode` from `deductionsUIStore`.
 */
export function DeductionGroupingMenu() {
  const { groupingMode, setGroupingMode } = useDeductionsUIStore(
    useShallow(state => ({
      groupingMode: state.groupingMode,
      setGroupingMode: state.setGroupingMode,
    }))
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleGroupingChange = useCallback(
    (event: SelectChangeEvent) => {
      setGroupingMode(event.target.value as DeductionGroupingMode);
    },
    [setGroupingMode]
  );

  return (
    <>
      <PageHeaderAction
        label="Display"
        onClick={handleOpen}
        icon={<TuneIcon />}
        aria-haspopup={true}
        aria-expanded={open}
        data-testid="deduction-display-button"
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 280,
              borderRadius: 2,
              p: 2,
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewListIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Grouping
            </Typography>
          </Box>
          <Select
            size="small"
            value={groupingMode}
            onChange={handleGroupingChange}
            data-testid="grouping-select"
            sx={{
              minWidth: 140,
              fontSize: 13,
              bgcolor: theme =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.06)',
              borderRadius: 1.5,
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-select': { py: 0.75, px: 1.5 },
            }}
          >
            <MenuItem value="worker">Worker / Gang</MenuItem>
            <MenuItem value="site">Site</MenuItem>
          </Select>
        </Box>
      </Popover>
    </>
  );
}
